import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import CompanyListViewData from './companyListViewData';
import { useNavigate } from 'react-router-dom';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
  name: 250, address: 250
};

const ListView = React.memo(({ scrollRef, companies, setOffset, sortBy, filteredCompaniesCount, setCompaniesFetched, setSortBy }) => {

  const navigate = useNavigate();
  const tableHeadRef = useRef(null);

  const [companyColumnWidth, setCompanyColumnWidth] = useState(() => {
    const storedWidths = JSON.parse(localStorage.getItem('companyColumnWidth')) || {};
    return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
  });

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // Update window height on resize
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowHeight(window.innerHeight);
    }, 300); // 300ms debounce delay

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // Cancel any pending debounced calls
    };
  }, []);

  // synchronize horizontal scroll between table head and list
  useSyncHorizontalScroll(tableHeadRef, scrollRef);

  // Calculate fetched items on mount
  useCalculateFetchedItems(filteredCompaniesCount, setCompaniesFetched, 70, 280);

  // Handle scroll and fetch more items
  const handleScrollList = useScrollFetchData(setOffset, setCompaniesFetched, filteredCompaniesCount, 70, 280);

  const handleCompanyClick = useCallback((id) => navigate(`entreprise_details/${id}`), [navigate]);

  const handleSort = (field) => {
    setSortBy((prevSortBy) => {
      const nextSortOrder = prevSortBy[field] === null ? 'asc'
        : prevSortBy[field] === 'asc' ? 'desc'
          : null;

      return Object.fromEntries(
        Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
      );
    });

  };

  const handleColumnWidthChange = (value, columnKey) => setCompanyColumnWidth((prevState) => ({ ...prevState, [columnKey]: value }));

  useEffect(() => localStorage.setItem('companyColumnWidth', JSON.stringify(companyColumnWidth)), [companyColumnWidth]);

  const Row = ({ index, style, data }) => {
    const { companies, handleCompanyClick } = data;
    const company = index === 0 ? companies[0] : companies[index];
    return (
      <Box key={index} style={{ ...style, width: 'fit-content' }}>
        <CompanyListViewData
          company={company}
          columnWidth={companyColumnWidth}
          handleCompanyClick={handleCompanyClick}
        />
      </Box>
    );
  };

  return (
    <Box className='table'>
      <Box className='tableHead' ref={tableHeadRef}>
        <CustomHeaderCell
          className='table_header_cell_non_filter'
          label='Name'
          columnKey='name'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          columnWidth={companyColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          className='table_header_cell_non_filter'
          label='Address'
          columnKey='address'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={false}
          columnWidth={companyColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          className='table_header_cell_non_filter' width="50px" />
      </Box>
      <Box className='tableBody'>
        <FixedSizeList
          ref={scrollRef}
          height={Math.min(
            filteredCompaniesCount * 70 + 15,
            windowHeight - 285
          )}
          width='100%'
          itemCount={filteredCompaniesCount}
          itemSize={70}
          itemData={{ companies, handleCompanyClick }}
          onScroll={handleScrollList}
        >
          {Row}
        </FixedSizeList>
      </Box>
    </Box>

  );
});

export default ListView;